import { DELIVERY_OPTIONS } from "../constants/DeliveryConstants";
import { ORDER_STATUS_KLARNA_AUTHORIZED, ORDER_STATUS_PAID, ORDER_STATUS_SHIPPED } from "../constants/OrderConstant";
import { getCarrierBooking } from "./carrierBooking";
import { isRegionalOwnDelivery, isTrunkrsDeliveryDate } from "./workDay";

/**
 * What has already been done for an order on its work day: is the packing slip printed, is the
 * shipping label printed (planning/werkdag-en-printstatus.md, deel 2).
 *
 * The warehouse prints in the early morning and again after 12:00, often by someone else on the
 * same shared account. Nothing recorded what the first round printed, and after a second SooCool
 * export every order reads "Verzonden", so the second person could not tell what was left. The CMS
 * now writes one `fulfilment` map on the order whenever it prints for it:
 *
 *   fulfilment: {
 *     packingSlipPrintedAt, packingSlipPrintCount,   // unix seconds, like createdAt
 *     packingSlipOutdated,                           // edited after printing: print again
 *     labelPrintedAt, labelPrintCount,
 *     labelPrintedFor,                               // the SooCool order id, or "own" (A6 label)
 *   }
 *
 * "Printed" means the print dialog closed or the PDF was downloaded - the browser does not tell
 * whether paper came out. The page marks right away and offers "Ongedaan maken" (Sam, 2026-10-15).
 *
 * Picking is deliberately not tracked here: that comes with the pick app (HVC-42).
 *
 * Pure: no Firestore, no React.
 */

export const FULFILMENT_FIELD = "fulfilment";

/** `labelPrintedFor` of our own A6 label (SendLabel.js), as opposed to a SooCool order id. */
export const OWN_LABEL = "own";

export const LABEL_KIND = {
  /** Has a SooCool shipment: only SooCool's PDF label counts, it carries the barcode they scan. */
  SOOCOOL: "soocool",
  /** Trunkrs (Monday), OptimoRoute (regional, our own van) and pickups: our own A6 label (Sam, 2026-10-15). */
  OWN: "own",
  /** A Tuesday-to-Saturday send order not yet exported: no label exists until SooCool has it. */
  AWAITING_EXPORT: "awaiting_export",
};

export const PRINT_STATE = {
  DONE: "done",
  TODO: "todo",
  /** Printed once, but no longer valid: edited afterwards, or the SooCool shipment was replaced. */
  REDO: "redo",
  /** Cannot be printed yet (the label of an order still waiting for its SooCool export). */
  BLOCKED: "blocked",
};

/** The statuses an order can be worked on in: the same set the carrier exports accept. */
const WORKABLE_STATUSES = [ORDER_STATUS_PAID, ORDER_STATUS_KLARNA_AUTHORIZED, ORDER_STATUS_SHIPPED];

export const isWorkableOrder = (order) =>
  !!order && WORKABLE_STATUSES.includes(order.orderStatus) && !order.containsOnlyGiftCards;

const isSendOrder = (order) => order?.delivery?.type === DELIVERY_OPTIONS.SEND;

export const labelKindFor = (order) => {
  if (order?.soocool?.orderId) return LABEL_KIND.SOOCOOL;
  if (!isSendOrder(order)) return LABEL_KIND.OWN;
  // Booked elsewhere: Trunkrs or OptimoRoute, both on our own label.
  if (getCarrierBooking(order)) return LABEL_KIND.OWN;
  // Monday is Trunkrs, and our A6 label does not depend on the Trunkrs export.
  if (isTrunkrsDeliveryDate(order?.delivery?.date)) return LABEL_KIND.OWN;
  // Regional goes with our own van (HVC-83); the label does not wait for the OptimoRoute export either.
  if (isRegionalOwnDelivery(order)) return LABEL_KIND.OWN;
  return LABEL_KIND.AWAITING_EXPORT;
};

/** The `labelPrintedFor` a valid label of this order carries; null when there can be none yet. */
export const expectedLabelFor = (order) => {
  const kind = labelKindFor(order);
  if (kind === LABEL_KIND.SOOCOOL) return String(order.soocool.orderId);
  if (kind === LABEL_KIND.OWN) return OWN_LABEL;
  return null;
};

export const packingSlipState = (order) => {
  const fulfilment = order?.fulfilment;
  if (!fulfilment?.packingSlipPrintedAt) return PRINT_STATE.TODO;
  if (fulfilment.packingSlipOutdated) return PRINT_STATE.REDO;
  return PRINT_STATE.DONE;
};

/**
 * A label printed for a different shipment is a label for the wrong parcel: SooCool cancelled and
 * re-exported (new order id, so the old label belongs to the cancelled one), or an order that moved
 * from SooCool to Trunkrs. Both read as REDO, never as DONE.
 */
export const labelState = (order) => {
  const expected = expectedLabelFor(order);
  if (expected === null) return PRINT_STATE.BLOCKED;
  const fulfilment = order?.fulfilment;
  if (!fulfilment?.labelPrintedAt) return PRINT_STATE.TODO;
  if (String(fulfilment.labelPrintedFor) !== expected) return PRINT_STATE.REDO;
  return PRINT_STATE.DONE;
};

export const isBookedWithCarrier = (order) => !!getCarrierBooking(order);

/** Anything left to do for this order: a carrier, a label or a packing slip. */
export const hasWorkLeft = (order) =>
  isWorkableOrder(order) &&
  ((isSendOrder(order) && !isBookedWithCarrier(order)) ||
    labelState(order) !== PRINT_STATE.DONE ||
    packingSlipState(order) !== PRINT_STATE.DONE);

// --- writing -----------------------------------------------------------------------------------

export const stampPackingSlip = (fulfilment, now) => ({
  ...(fulfilment || {}),
  packingSlipPrintedAt: now,
  packingSlipPrintCount: (fulfilment?.packingSlipPrintCount || 0) + 1,
  packingSlipOutdated: false,
});

export const stampLabel = (fulfilment, labelFor, now) => ({
  ...(fulfilment || {}),
  labelPrintedAt: now,
  labelPrintCount: (fulfilment?.labelPrintCount || 0) + 1,
  labelPrintedFor: labelFor,
});

/**
 * The `fulfilment` map after an edit that changes what is on the packing slip, or null when there
 * is nothing to mark: a slip that was never printed cannot be outdated.
 */
export const markPackingSlipOutdated = (fulfilment) =>
  fulfilment?.packingSlipPrintedAt ? { ...fulfilment, packingSlipOutdated: true } : null;

/**
 * The writes for one print run: per order the new `fulfilment` map, plus the old one so the run
 * can be undone. Orders the run does not change are left out.
 *
 * - `packingSlip`: every order in the run gets the packing slip stamp.
 * - `label: OWN_LABEL`: our A6 label, stamped only on orders that ship on it. A SooCool order in an
 *   "Alle" run gets an A6 sheet too, but that is not its label.
 * - `label: LABEL_KIND.SOOCOOL`: SooCool's PDF, stamped with the shipment it was printed for.
 */
export const planPrintStamps = (orders, { packingSlip = false, label = null } = {}, now) =>
  (orders || []).flatMap((order) => {
    if (!order?.id || !order?.shopSource) return [];
    const previous = order.fulfilment || null;
    let next = previous;
    if (packingSlip) next = stampPackingSlip(next, now);
    if (label === OWN_LABEL && labelKindFor(order) === LABEL_KIND.OWN) next = stampLabel(next, OWN_LABEL, now);
    if (label === LABEL_KIND.SOOCOOL && order.soocool?.orderId) {
      next = stampLabel(next, String(order.soocool.orderId), now);
    }
    return next === previous ? [] : [{ order, previous, next }];
  });

// --- the "Nog te doen" bar ---------------------------------------------------------------------

const byCreatedAt = (a, b) => (a.createdAt || 0) - (b.createdAt || 0);

/**
 * Where a work day stands, per step, with the orders each step still has to do - in the order the
 * packing table uses (oldest first), so labels and packing slips of one run come out in step.
 */
export const workDayProgress = (orders) => {
  const workable = (orders || []).filter(isWorkableOrder).sort(byCreatedAt);
  const sends = workable.filter(isSendOrder);
  const unbooked = sends.filter((order) => !isBookedWithCarrier(order));
  const labelsDone = workable.filter((order) => labelState(order) === PRINT_STATE.DONE);
  const labelsOpen = workable.filter((order) => labelState(order) !== PRINT_STATE.DONE);

  return {
    total: workable.length,
    carrier: {
      total: sends.length,
      done: sends.length - unbooked.length,
      toSooCool: unbooked.filter(
        (order) => !isTrunkrsDeliveryDate(order.delivery?.date) && !isRegionalOwnDelivery(order),
      ),
      toTrunkrs: unbooked.filter((order) => isTrunkrsDeliveryDate(order.delivery?.date)),
      // Regional, our own van (HVC-83). Never on a Monday: a regional customer cannot pick Monday.
      toOptimoRoute: unbooked.filter((order) => isRegionalOwnDelivery(order)),
    },
    labels: {
      total: workable.length,
      done: labelsDone.length,
      soocool: labelsOpen.filter((order) => labelKindFor(order) === LABEL_KIND.SOOCOOL),
      own: labelsOpen.filter((order) => labelKindFor(order) === LABEL_KIND.OWN),
      awaitingExport: labelsOpen.filter((order) => labelState(order) === PRINT_STATE.BLOCKED),
    },
    packingSlips: {
      total: workable.length,
      done: workable.filter((order) => packingSlipState(order) === PRINT_STATE.DONE).length,
      open: workable.filter((order) => packingSlipState(order) !== PRINT_STATE.DONE),
    },
  };
};
